import { useId, useState } from 'react';
import type React from 'react';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { FOKUS } from './klassen';

/**
 * Rahmen und Bausteine eines GEFUEHRTEN Dialogs — eine Karte je Schritt,
 * oben die schmale Fortschrittsleiste, unten leise der Fuss.
 *
 * Entstanden in der Planungsphase (`planungsphase/dialog/DialogKarten.tsx`,
 * 01.11.2026) und am 02.11.2026 hierher gezogen, damit die Baustellenordnung
 * denselben Rahmen benutzt und nicht einen zweiten, der auseinanderlaeuft.
 * Die Planungsphase importiert von hier; ihr `DialogRahmen` bildet nur noch
 * ihre Abschnitte auf `schritte` ab.
 *
 * Regeln der Karten (gelten fuer jeden Aufrufer): hoechstens ein Satz
 * Erklaerung, genau ein grosser Hauptknopf, hoechstens zwei leise
 * Textknoepfe. Eigener Zustand ist nur Eingabe; welche Karte dran ist,
 * entscheidet der Container des Moduls.
 */

export type SchrittStand = 'erledigt' | 'aktuell' | 'offen';

export interface GefuehrtSchritt {
    label: string;
    stand: SchrittStand;
}

export interface GefuehrtRahmenProps {
    schritte: readonly GefuehrtSchritt[];
    /** Die Karte. */
    children: React.ReactNode;
    /** Leise Zeile unter der Karte. */
    fuss?: React.ReactNode;
}

// Die Knopf- und Feldklassen stehen in `klassen.ts` (react-refresh).

// ═══════════════════════════════════════════════════════════════════════════
// BAUSTEINE
// ═══════════════════════════════════════════════════════════════════════════

export const Kreisel: React.FC<{ klein?: boolean }> = ({ klein }) => (
    <Loader2 className={`${klein ? 'w-4 h-4' : 'w-6 h-6 text-blue-600'} animate-spin`} />
);

/** Kopf einer Karte: Titel als Ueberschrift, darunter hoechstens ein Satz. */
export const Kopf: React.FC<{
    id?: string;
    titel: React.ReactNode;
    zeile?: React.ReactNode;
    /** Kleine graue Zeile ueber dem Titel („Frage 3 von 14"). */
    ueber?: React.ReactNode;
    icon?: React.ReactNode;
    /** Die Frage selbst — eine Stufe groesser. */
    gross?: boolean;
    titelTon?: string;
    rolle?: 'status' | 'alert';
}> = ({ id, titel, zeile, ueber, icon, gross, titelTon = 'text-slate-900', rolle }) => (
    <div role={rolle} className="flex items-start gap-3">
        {icon && <span aria-hidden="true" className="mt-0.5 shrink-0">{icon}</span>}
        <div className="min-w-0 flex-1">
            {ueber && <p className="text-[11px] font-black uppercase tracking-wider text-slate-400">{ueber}</p>}
            <h2
                id={id}
                className={`${ueber ? 'mt-1 ' : ''}${gross ? 'text-xl sm:text-2xl' : 'text-lg sm:text-xl'} font-black leading-tight break-words ${titelTon}`}
            >
                {titel}
            </h2>
            {zeile && <p className="mt-1.5 text-sm text-slate-600 leading-snug break-words">{zeile}</p>}
        </div>
    </div>
);

/** Fuss einer Karte: Hauptknopf, daneben (am Telefon darunter) die leisen Knoepfe. */
export const Handlungen: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2">{children}</div>
);

/** Aufklapper „Warum?" bzw. Beleg: zugeklappt, der Inhalt steht schon im DOM. */
export const Aufklapper: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
    const [offen, setOffen] = useState(false);
    const inhaltId = useId();
    return (
        <div className="mt-2">
            <button
                type="button"
                onClick={() => setOffen(o => !o)}
                aria-expanded={offen}
                aria-controls={inhaltId}
                className={`inline-flex min-h-9 items-center gap-1 rounded-lg text-sm font-bold text-blue-700 hover:text-blue-900 ${FOKUS}`}
            >
                {label}
                <ChevronDown className={`w-4 h-4 transition-transform ${offen ? 'rotate-180' : ''}`} />
            </button>
            <div id={inhaltId} hidden={!offen} className="mt-1 text-sm text-slate-600 leading-snug break-words">
                {children}
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// RAHMEN
// ═══════════════════════════════════════════════════════════════════════════

const STAND_TEXT: Record<SchrittStand, string> = {
    erledigt: 'text-slate-600',
    aktuell: 'text-blue-700',
    offen: 'text-slate-400',
};

const STAND_PUNKT: Record<SchrittStand, string> = {
    erledigt: 'bg-emerald-100 text-emerald-700',
    aktuell: 'bg-blue-600 text-white',
    offen: 'bg-slate-100 text-slate-400',
};

/**
 * Rahmen des Dialogs: oben die schmale Leiste der Schritte (bricht am
 * Telefon um), darunter die eine Karte, darunter leise der Fuss.
 */
export const GefuehrtRahmen: React.FC<GefuehrtRahmenProps> = ({ schritte, children, fuss }) => (
    <div className="mx-auto w-full max-w-[40rem] space-y-3">
        <ol aria-label="Fortschritt" className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-1">
            {schritte.map(({ label, stand }, i) => (
                <li
                    key={label}
                    aria-current={stand === 'aktuell' ? 'step' : undefined}
                    className={`inline-flex items-center gap-1.5 text-xs font-bold ${STAND_TEXT[stand]}`}
                >
                    <span
                        aria-hidden="true"
                        className={`inline-flex w-5 h-5 shrink-0 items-center justify-center rounded-full text-[10px] font-black ${STAND_PUNKT[stand]}`}
                    >
                        {stand === 'erledigt' ? <Check className="w-3 h-3" strokeWidth={3} /> : i + 1}
                    </span>
                    {label}
                    {stand === 'erledigt' && <span className="sr-only"> (erledigt)</span>}
                </li>
            ))}
        </ol>
        <section data-glass="karte" className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6">
            {children}
        </section>
        {fuss ? <div className="px-1 text-xs text-slate-500 leading-snug">{fuss}</div> : null}
    </div>
);
